import { api } from './client'
import { todayISO } from '@/lib/dates'
import { isNative } from '@/native'
import type {
  Bucket, Priority, PriorityHistory, ValueTag, PriorityValueTag, Note,
  JournalEntry, JournalLink, JournalBucketLink, WeeklyReview, TimeLog,
  AppSettings, AiSummary,
} from '@/types'

export interface ExportPayload {
  exportedAt: string
  version: number
  buckets: Bucket[]
  priorities: Priority[]
  priorityHistory: PriorityHistory[]
  valueTags: ValueTag[]
  priorityValueTags: PriorityValueTag[]
  notes: Note[]
  journalEntries: JournalEntry[]
  journalLinks: JournalLink[]
  journalBucketLinks: JournalBucketLink[]
  weeklyReviews: WeeklyReview[]
  timeLogs: TimeLog[]
  appSettings: AppSettings[]
  aiSummaries: AiSummary[]
}

export function fetchExportData(): Promise<ExportPayload> {
  return api.get('/api/data/export')
}

export function importData(payload: ExportPayload): Promise<void> {
  return api.post('/api/data/import', payload)
}

export function deleteAllData(): Promise<void> {
  return api.post('/api/data/delete-all')
}

/** Saves the current server data as a JSON file: a browser download, or the iOS share sheet. */
export async function downloadExport(): Promise<void> {
  const payload = await fetchExportData()
  const fileName = `plumb-export-${todayISO()}.json`
  const json = JSON.stringify(payload, null, 2)
  if (isNative) {
    const { shareTextFile } = await import('@/native/files')
    await shareTextFile(fileName, json)
    return
  }
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function validateImportPayload(data: unknown): ExportPayload {
  if (!data || typeof data !== 'object') {
    throw new Error('Not a valid Plumb export file.')
  }
  const p = data as Record<string, unknown>
  if (typeof p['version'] !== 'number') {
    throw new Error('Missing version field — is this a Plumb export file?')
  }
  if (!Array.isArray(p['buckets'])) {
    throw new Error('Missing buckets array — is this a Plumb export file?')
  }
  const arr = <T,>(key: string): T[] => (Array.isArray(p[key]) ? (p[key] as T[]) : [])
  return {
    exportedAt: typeof p['exportedAt'] === 'string' ? p['exportedAt'] : new Date().toISOString(),
    version: p['version'] as number,
    buckets: arr<Bucket>('buckets'),
    priorities: arr<Priority>('priorities'),
    priorityHistory: arr<PriorityHistory>('priorityHistory'),
    valueTags: arr<ValueTag>('valueTags'),
    priorityValueTags: arr<PriorityValueTag>('priorityValueTags'),
    notes: arr<Note>('notes'),
    journalEntries: arr<JournalEntry>('journalEntries'),
    journalLinks: arr<JournalLink>('journalLinks'),
    journalBucketLinks: arr<JournalBucketLink>('journalBucketLinks'),
    weeklyReviews: arr<WeeklyReview>('weeklyReviews'),
    timeLogs: arr<TimeLog>('timeLogs'),
    appSettings: arr<AppSettings>('appSettings'),
    aiSummaries: arr<AiSummary>('aiSummaries'),
  }
}
